import { S3Client } from "@aws-sdk/client-s3";
import { NodeHttpHandler } from "@smithy/node-http-handler";
import { transformS3Path } from "./transformPath";

/**
 * Gets the base S3 client configuration for Build0 Blob Storage
 * @returns S3 client configuration object
 */
const getS3ClientConfig = () => {
  return {
    region: "auto",
    endpoint: process.env.BUILD0_BLOB_URL!,
    credentials: {
      accessKeyId: "unused",
      secretAccessKey: "unused",
    },
    forcePathStyle: true,
    useAccelerateEndpoint: false, // Disable S3 Transfer Acceleration (not supported by custom endpoints)
  };
};

/**
 * Gets the S3 bucket name from environment
 * @returns Bucket name (defaults to "blob")
 */
export const getS3BucketName = () => {
  return process.env.BUILD0_BLOB_BUCKET_NAME ?? "blob";
};

/**
 * Creates an S3 client that forwards session cookies from the incoming request
 * This is required for Build0 Blob Storage endpoints that need authentication
 *
 * @param reqHeaders - Request headers containing the Cookie header
 * @returns S3Client configured with cookie forwarding
 */
export function createS3Client(reqHeaders: Headers | undefined): S3Client {
  const cfg: any = getS3ClientConfig();
  const cookie = reqHeaders?.get("cookie") || "";

  const client = new S3Client({
    ...cfg,
    requestHandler: new NodeHttpHandler({
      requestTimeout: 60000, // 60 seconds for large uploads
    }),
  });

  // Add middleware to strip Expect header, add Cookie, and transform path
  client.middlewareStack.add(
    (next: any) => async (args: any) => {
      // Remove Expect: 100-continue header that causes issues
      if (args.request?.headers) {
        delete args.request.headers["expect"];
        delete args.request.headers["Expect"];
      }

      // Add Cookie header for authentication
      if (cookie) {
        (args.request as any).headers["Cookie"] = cookie;
      }

      // Transform path: strip trailing slashes to avoid routing issues
      if (args.request?.path) {
        args.request.path = transformS3Path(args.request.path);
      }

      return await next(args);
    },
    { step: "build", name: "customHeaders" }
  );

  return client;
}
